import { useCallback, useRef, useState } from 'react';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ATTENDANCE_EVENT_TYPE } from '@src/packages/workJourney/constants';
import AttendanceEventService from '@src/services/AttendanceEventService';
import PrepareAttendanceSessionService from '@src/services/PrepareAttendanceSessionService';
import UserWorkJourneyScheduleService from '@src/services/UserWorkJourneyScheduleService';
import { formatUserFullName } from '@src/util/format/userFullName';

import type { MarkAttendanceJourneyDetails, MarkAttendanceUserRow } from '../types';
import { clampMarkedAtToNow } from '../utils/clampMarkedAtToNow';
import { createShiftStartAttendanceEvent } from '../utils/createShiftStartAttendanceEvent';
import {
  mapNewAttendanceEventError,
  NEW_ATTENDANCE_EVENT_ERROR_MESSAGE_ID,
} from '../utils/mapNewAttendanceEventError';

type UseStartJourneyConfirmParams = {
  onSuccess?: () => void;
};

const toEmployeeId = (user: MarkAttendanceUserRow | null): string => user?.id?.trim() ?? '';

export const useStartJourneyConfirm = ({ onSuccess }: UseStartJourneyConfirmParams = {}) => {
  const intl = useIntl();
  const orgId = useSelector(
    (state: { organizations: { organization: { id: string } } }) =>
      state.organizations.organization.id,
  );
  const actorId = useSelector(
    (state: { user?: { profile?: { data?: { id?: string } } } }) =>
      state.user?.profile?.data?.id ?? '',
  );

  const [user, setUser] = useState<MarkAttendanceUserRow | null>(null);
  const [journeyDetails, setJourneyDetails] = useState<MarkAttendanceJourneyDetails | null>(null);
  const [markedAt, setMarkedAt] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const scheduleServiceRef = useRef<UserWorkJourneyScheduleService | null>(null);
  if (scheduleServiceRef.current === null) {
    scheduleServiceRef.current = new UserWorkJourneyScheduleService();
  }
  const scheduleService = scheduleServiceRef.current;

  const attendanceEventServiceRef = useRef<AttendanceEventService | null>(null);
  if (attendanceEventServiceRef.current === null) {
    attendanceEventServiceRef.current = new AttendanceEventService();
  }
  const attendanceEventService = attendanceEventServiceRef.current;

  const prepareAttendanceSessionServiceRef = useRef<PrepareAttendanceSessionService | null>(null);
  if (prepareAttendanceSessionServiceRef.current === null) {
    prepareAttendanceSessionServiceRef.current = new PrepareAttendanceSessionService();
  }
  const prepareAttendanceSessionService = prepareAttendanceSessionServiceRef.current;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const scheduleFetchRequestIdRef = useRef(0);

  const reset = useCallback(() => {
    scheduleFetchRequestIdRef.current += 1;
    setUser(null);
    setJourneyDetails(null);
    setMarkedAt(null);
    setSubmitting(false);
  }, []);

  const openForUser = useCallback(
    (nextUser: MarkAttendanceUserRow, seedDetails?: MarkAttendanceJourneyDetails | null) => {
      const employeeId = toEmployeeId(nextUser);
      if (!employeeId) {
        return;
      }
      if (!actorId) {
        notification.error({
          message: intl.formatMessage({
            id: 'workJourney.realTime.markAttendance.error.permissionDenied',
          }),
        });
        return;
      }

      const requestId = ++scheduleFetchRequestIdRef.current;
      setUser(nextUser);
      setMarkedAt(new Date());
      setSubmitting(false);

      if (seedDetails) {
        setJourneyDetails(seedDetails);
        return;
      }

      setJourneyDetails(null);
      const orgIdAtOpen = orgId;
      void (async () => {
        try {
          const details = await scheduleService.getScheduleDetails(orgIdAtOpen, employeeId);
          if (scheduleFetchRequestIdRef.current !== requestId) {
            return;
          }
          setJourneyDetails(details ?? null);
        } catch {
          if (scheduleFetchRequestIdRef.current !== requestId) {
            return;
          }
          setJourneyDetails(null);
        }
      })();
    },
    [actorId, intl, orgId, scheduleService],
  );

  const handleCancel = useCallback(() => {
    if (submitting) {
      return;
    }
    reset();
  }, [reset, submitting]);

  const handleConfirm = useCallback(async () => {
    const employeeId = toEmployeeId(user);
    if (!user || !employeeId || !orgId || !actorId || !markedAt) {
      return;
    }

    setSubmitting(true);
    try {
      await createShiftStartAttendanceEvent({
        organizationId: orgId,
        actorId,
        employeeId,
        markedAt: clampMarkedAtToNow(markedAt),
        attendanceRecordId: journeyDetails?.attendanceRecordId ?? null,
        attendanceEventService,
        prepareAttendanceSessionService,
      });
      reset();
      notification.success({
        message: intl.formatMessage({
          id: 'workJourney.realTime.markAttendance.success',
        }),
      });
      onSuccessRef.current?.();
    } catch (error) {
      const errorKey = mapNewAttendanceEventError(error);
      notification.error({
        message: intl.formatMessage({
          id: NEW_ATTENDANCE_EVENT_ERROR_MESSAGE_ID[errorKey],
        }),
      });
      setSubmitting(false);
    }
  }, [
    actorId,
    attendanceEventService,
    intl,
    journeyDetails?.attendanceRecordId,
    markedAt,
    orgId,
    prepareAttendanceSessionService,
    reset,
    user,
  ]);

  const confirmKind = user ? ATTENDANCE_EVENT_TYPE.SHIFT_START : null;
  const userDisplayName = user
    ? formatUserFullName(user) || user.fullName?.trim() || ''
    : '';

  const handleMarkedAtChange = useCallback((next: Date) => {
    setMarkedAt(clampMarkedAtToNow(next));
  }, []);

  return {
    open: user != null,
    confirmKind,
    userDisplayName,
    markedAt,
    journeyDetails,
    submitting,
    openForUser,
    handleConfirm,
    handleCancel,
    handleMarkedAtChange,
  };
};
